import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

import { books } from "../data/books";
import { bookPath } from "../lib/paths";

const T = {
  de: {
    rotulo: "Katalog",
    title: "Bücher",
    subtitle:
      "Erzählliteratur auf Spanisch und auf Deutsch. Wenige Titel im Jahr, jeder im Haus lektoriert, gesetzt und gestaltet.",
    more: "Zum Buch",
    soon: "Erscheint",
    available: "Erhältlich",
    metaTitle: "Bücher – Blauer Wolf Verlag",
    metaDesc:
      "Die Titel des Blauer Wolf Verlags: Erzählliteratur auf Spanisch und Deutsch, verlegt in Oberösterreich.",
  },
  es: {
    rotulo: "Catálogo",
    title: "Libros",
    subtitle:
      "Narrativa en español y en alemán. Pocos títulos al año, cada uno corregido, maquetado y diseñado en casa.",
    more: "Ver el libro",
    soon: "Publicación",
    available: "Disponible",
    metaTitle: "Libros – Blauer Wolf Verlag",
    metaDesc:
      "Los títulos de Blauer Wolf Verlag: narrativa en español y alemán, editada en Alta Austria.",
  },
};

export default function Books({ language = "de" }) {
  const t = T[language];

  return (
    <div className="bg-papel text-tinta min-h-[70vh]">
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-16"
        >
          <p className="rotulo text-loboOscuro mb-5">{t.rotulo}</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-titulo mb-6">
            {t.title}
          </h1>
          <p className="text-lg text-tinta/80">{t.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-12">
          {books.map((b, i) => {
            const c = b[language];
            return (
              <motion.article
                key={b.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + i * 0.08, duration: 0.5 }}
              >
                <Link to={bookPath(language, b.slug)} className="group block">
                  <img
                    src={b.coverSmall || b.cover}
                    alt={b.title}
                    width="320"
                    height="495"
                    loading="lazy"
                    className="w-full mb-6 shadow-[0_14px_40px_-16px_rgba(18,16,14,0.55)] group-hover:shadow-[0_18px_48px_-16px_rgba(18,16,14,0.7)] transition-shadow duration-300"
                  />
                  <p className="rotulo text-sombra mb-2">{c.genre}</p>
                  <h2 className="font-serif text-2xl leading-snug mb-1 group-hover:text-loboOscuro transition-colors">
                    {b.title}
                  </h2>
                  {b.subtitle && (
                    <p className="font-serif italic text-sombra/90 mb-2">{b.subtitle}</p>
                  )}
                  <p className="text-sm text-sombra mb-4">{b.author}</p>
                  <p className="text-tinta/75 mb-5">{c.tagline}</p>
                  {/* Con dos o más libros por fila la tarjeta se estrecha: el pie
                      se apila en vez de partir «Ver el libro» de su flecha. */}
                  <div className="flex flex-col gap-2 pt-4 border-t border-tinta/15 min-[420px]:flex-row min-[420px]:items-baseline min-[420px]:justify-between min-[420px]:gap-4">
                    <span className="rotulo whitespace-nowrap text-loboOscuro group-hover:underline underline-offset-4">
                      {t.more}&nbsp;→
                    </span>
                    <span className="rotulo text-sombra min-[420px]:text-right">
                      {b.available
                        ? t.available
                        : `${t.soon} · ${b.releaseLabel[language]}`}
                    </span>
                  </div>
                </Link>
              </motion.article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
